import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, HelpCircle, Loader2, Search, Sparkles } from 'lucide-react';
import { helpApi } from '@/services/api';
import HelpArticleView, { type HelpArticleDetail } from './HelpArticleView';
import HelpFaqSection, { type HelpFaqItem } from './HelpFaqSection';

type Category = {
  name: string;
  title: string;
  slug: string;
  kind: string;
  icon?: string;
};

type ArticleSummary = {
  name: string;
  title: string;
  slug: string;
  category: string;
  summary?: string;
};

type Props = {
  initialArticleSlug?: string | null;
  onArticleChange?: (slug: string | null) => void;
};

export default function KnowledgeHub({ initialArticleSlug, onArticleChange }: Props) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [categorySlug, setCategorySlug] = useState<string | null>(null);
  const [articleSlug, setArticleSlug] = useState<string | null>(initialArticleSlug || null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), 250);
    return () => window.clearTimeout(t);
  }, [query]);

  useEffect(() => {
    setArticleSlug(initialArticleSlug || null);
  }, [initialArticleSlug]);

  const selectArticle = (slug: string | null) => {
    setArticleSlug(slug);
    onArticleChange?.(slug);
  };

  const { data: categories = [], isLoading: catsLoading } = useQuery({
    queryKey: ['helpCategories'],
    queryFn: async () => (await helpApi.listCategories()).data.message as Category[],
  });

  const { data: articles = [], isLoading: artsLoading } = useQuery({
    queryKey: ['helpArticles', categorySlug],
    queryFn: async () =>
      (await helpApi.listArticles(categorySlug || undefined)).data.message as ArticleSummary[],
  });

  const { data: faqs = [], isLoading: faqsLoading } = useQuery({
    queryKey: ['helpFaqs', categorySlug],
    queryFn: async () =>
      (await helpApi.listFaqs(categorySlug || undefined)).data.message as HelpFaqItem[],
  });

  const { data: searchResult, isFetching: searching } = useQuery({
    queryKey: ['helpSearch', debounced],
    queryFn: async () => (await helpApi.search(debounced)).data.message as {
      articles: ArticleSummary[];
      faqs: HelpFaqItem[];
      query: string;
    },
    enabled: debounced.length >= 2,
  });

  const { data: articleDetail, isLoading: articleLoading } = useQuery({
    queryKey: ['helpArticle', articleSlug],
    queryFn: async () =>
      (await helpApi.getArticle(articleSlug!)).data.message as HelpArticleDetail,
    enabled: Boolean(articleSlug),
  });

  const manualCategories = useMemo(
    () => categories.filter((c) => c.kind === 'Manual' || c.kind === 'Both'),
    [categories],
  );

  const showSearch = debounced.length >= 2;
  const listArticles = showSearch ? searchResult?.articles || [] : articles;
  const listFaqs = showSearch ? searchResult?.faqs || [] : faqs;

  if (articleSlug) {
    return (
      <div className="space-y-4">
        {articleLoading || !articleDetail ? (
          <div className="flex justify-center py-16 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <HelpArticleView article={articleDetail} onBack={() => selectArticle(null)} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-primary-100 bg-gradient-to-br from-primary-50/80 to-white p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-xl bg-primary-600 text-white flex items-center justify-center shrink-0">
            <BookOpen className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-black text-gray-900 uppercase tracking-wide">Knowledge Base</h2>
            <p className="text-xs text-gray-500">POS manual and answers to common questions</p>
          </div>
        </div>
        <div className="relative w-full sm:max-w-xs">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            className="w-full h-10 pl-9 pr-3 border border-gray-200 rounded-lg text-sm focus:ring-[3px] focus:ring-primary-500/20 focus:border-primary-600"
            placeholder="Search manual & Q&A…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="inline-flex items-center gap-2 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-[11px] text-gray-500">
        <Sparkles className="w-3.5 h-3.5 text-amber-500" />
        Ask SaleDesk AI assistant — coming soon
      </div>

      {!showSearch ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setCategorySlug(null)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
              !categorySlug
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
            }`}
          >
            All
          </button>
          {manualCategories.map((c) => (
            <button
              key={c.name}
              type="button"
              onClick={() => setCategorySlug(c.slug)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                categorySlug === c.slug
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              }`}
            >
              {c.title}
            </button>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-500">
          {searching ? 'Searching…' : `Results for “${debounced}”`}
        </p>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6 items-start">
        <section className="xl:col-span-3 space-y-3">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-primary-600" /> Manual
          </h3>
          {catsLoading || artsLoading ? (
            <div className="flex justify-center py-10 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : listArticles.length === 0 ? (
            <p className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-xl px-4 py-6 text-center">
              No manual articles found.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {listArticles.map((a) => (
                <button
                  key={a.name}
                  type="button"
                  onClick={() => selectArticle(a.slug)}
                  className="text-left rounded-xl border border-gray-200 bg-white p-4 hover:border-primary-300 hover:shadow-sm transition-all"
                >
                  <p className="text-sm font-bold text-gray-900">{a.title}</p>
                  {a.summary ? (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{a.summary}</p>
                  ) : null}
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="xl:col-span-2 space-y-3">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide flex items-center gap-2">
            <HelpCircle className="w-4 h-4 text-primary-600" /> Q&amp;A
          </h3>
          {faqsLoading && !showSearch ? (
            <div className="flex justify-center py-10 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <HelpFaqSection faqs={listFaqs} title="" />
          )}
        </section>
      </div>
    </div>
  );
}
